/**
 * Formulário de um colaborador.
 *
 * Papel: preenche, valida e grava uma pessoa do quadro — nome, apelido, CPF,
 * cargo, departamento, residência, status, datas e salário base.
 *
 * Como usar: quem monta este componente passa `colaborador` para editar ou
 * `null` para criar, e deverá dar a ele uma `key` diferente por pessoa. Sem a
 * `key`, o React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes.
 *
 * O CPF é campo mascarado e é obrigatório: ele é a chave que liga esta pessoa ao
 * favorecido de Finanças, e um cadastro sem ele nasce impossível de reconciliar.
 * O dígito verificador é conferido por `cpfSchema`, e não só o comprimento — um
 * CPF digitado errado passa despercebido por anos.
 *
 * O apelido não é obrigatório e **não é derivado do primeiro nome**. É por ele
 * que a escala do calendário alcança a pessoa, e dois homônimos de primeiro nome
 * receberiam o mesmo apelido — que é exatamente o que faz uma escala casar com
 * quem não devia.
 */
import {
  type Colaborador,
  type NovoColaborador,
  STATUS_COLABORADOR,
  novoColaboradorSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { useResidencias } from '@/modules/cadastros-base';
import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarColaborador } from '../hooks/usar-rh';
import { COLABORADOR_EM_BRANCO, type ColaboradorNoFormulario } from '../types';

interface FormularioColaboradorProps {
  /** O colaborador a editar, ou `null` para criar um novo. */
  colaborador: Colaborador | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<ColaboradorNoFormulario> | undefined> = {
  nomeCompleto: 'nomeCompleto',
  apelido: 'apelido',
  cpf: 'cpf',
  cargo: 'cargo',
  departamento: 'departamento',
  residenciaId: 'residenciaId',
  status: 'status',
  admissao: 'admissao',
  desligamento: 'desligamento',
  salario: 'salario',
  observacao: 'observacao',
};

/** Como cada situação aparece no campo de escolha. */
const ROTULO_DO_STATUS: Record<(typeof STATUS_COLABORADOR)[number], string> = {
  ativo: 'Ativo',
  desligado: 'Desligado',
  diarista: 'Diarista',
};

/**
 * Converte o colaborador vindo da API no formato que o formulário guarda.
 *
 * @param colaborador - A pessoa a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(colaborador: Colaborador | null): ColaboradorNoFormulario {
  if (colaborador === null) return { ...COLABORADOR_EM_BRANCO };

  return {
    nomeCompleto: colaborador.nomeCompleto,
    apelido: colaborador.apelido ?? '',
    cpf: colaborador.cpf,
    cargo: colaborador.cargo,
    departamento: colaborador.departamento ?? '',
    residenciaId: colaborador.residencia.id,
    status: colaborador.status,
    admissao: colaborador.admissao ?? '',
    desligamento: colaborador.desligamento ?? '',
    salario: colaborador.salario === null ? '' : String(colaborador.salario),
    observacao: colaborador.observacao ?? '',
  };
}

/**
 * O formulário de criação e de edição de colaborador.
 *
 * @param colaborador - A pessoa a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioColaborador({
  colaborador,
  onSalvo,
  onCancelar,
}: FormularioColaboradorProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const casas = useResidencias();
  const salvar = useSalvarColaborador();

  const form = useForm<ColaboradorNoFormulario, unknown, NovoColaborador>({
    resolver: zodResolver(novoColaboradorSchema),
    defaultValues: valoresIniciais(colaborador),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /** Grava o colaborador e leva o erro da API para o campo que o causou. */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: colaborador?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="colaborador-nome">Nome completo</Label>
          <Input
            id="colaborador-nome"
            autoComplete="off"
            disabled={enviando}
            {...form.register('nomeCompleto')}
          />
          {errors.nomeCompleto && (
            <p className="text-sm text-destructive" role="alert">
              {errors.nomeCompleto.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-apelido">Apelido</Label>
          <Input
            id="colaborador-apelido"
            autoComplete="off"
            placeholder="Como aparece na escala"
            disabled={enviando}
            {...form.register('apelido')}
          />
          {errors.apelido && (
            <p className="text-sm text-destructive" role="alert">
              {errors.apelido.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-cargo">Cargo</Label>
          <Input
            id="colaborador-cargo"
            autoComplete="off"
            disabled={enviando}
            {...form.register('cargo')}
          />
          {errors.cargo && (
            <p className="text-sm text-destructive" role="alert">
              {errors.cargo.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-cpf">CPF</Label>
          <Controller
            control={form.control}
            name="cpf"
            render={({ field }) => (
              <CampoMascarado
                id="colaborador-cpf"
                formato="cpf"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
          {errors.cpf && (
            <p className="text-sm text-destructive" role="alert">
              {errors.cpf.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-departamento">Departamento</Label>
          <Input
            id="colaborador-departamento"
            autoComplete="off"
            disabled={enviando}
            {...form.register('departamento')}
          />
          {errors.departamento && (
            <p className="text-sm text-destructive" role="alert">
              {errors.departamento.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-casa">Residência</Label>
          <Controller
            control={form.control}
            name="residenciaId"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={enviando || casas.isPending}
              >
                <SelectTrigger id="colaborador-casa" className="w-full">
                  <SelectValue
                    placeholder={casas.isPending ? 'Carregando…' : 'Escolha onde trabalha'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(casas.data ?? []).map((casa) => (
                    <SelectItem key={casa.id} value={casa.id}>
                      {casa.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.residenciaId && (
            <p className="text-sm text-destructive" role="alert">
              {errors.residenciaId.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-admissao">Admissão</Label>
          <Controller
            control={form.control}
            name="admissao"
            render={({ field }) => (
              <CampoMascarado
                id="colaborador-admissao"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="DD/MM/AAAA"
              />
            )}
          />
          {errors.admissao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.admissao.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-salario">Salário base (R$)</Label>
          <Controller
            control={form.control}
            name="salario"
            render={({ field }) => (
              <CampoMascarado
                id="colaborador-salario"
                formato="dinheiro"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
          <p className="text-xs text-muted-foreground">
            Em branco em quem presta serviço por diária.
          </p>
          {errors.salario && (
            <p className="text-sm text-destructive" role="alert">
              {errors.salario.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-status">Status</Label>
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="colaborador-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_COLABORADOR.map((status) => (
                    <SelectItem key={status} value={status}>
                      {ROTULO_DO_STATUS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.status && (
            <p className="text-sm text-destructive" role="alert">
              {errors.status.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="colaborador-desligamento">Data de desligamento</Label>
          <Controller
            control={form.control}
            name="desligamento"
            render={({ field }) => (
              <CampoMascarado
                id="colaborador-desligamento"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="DD/MM/AAAA"
              />
            )}
          />
          {errors.desligamento && (
            <p className="text-sm text-destructive" role="alert">
              {errors.desligamento.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="colaborador-observacao">Observações</Label>
          <Textarea
            id="colaborador-observacao"
            rows={3}
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar funcionário'}
        </Button>
      </DialogFooter>
    </form>
  );
}
